// @ts-nocheck
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class MultiStepForm extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.totalSteps = 0;
    this.productMediaUpdateRequestId = 0;
    this.productMediaGalleryCache = new Map();
    this.productMediaGalleryRequests = new Map();
    this.originalProductMediaKey = this.normalizeProductMediaKey(window.location.pathname);
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.mobileStickyCtaObserver = null;
    this.init();
  }

  init() {
    this.progressBar = this.querySelector('.multi-step-form__progress');
    this.stepsContainer = this.querySelector('.multi-step-form__steps');
    this.form = this.querySelector('.multi-step-form__form');
    this.handleEditorBlockSelect = this.handleEditorBlockSelect.bind(this);

    this.ensureMediaPromoBanner();
    this.updateSteps();
    this.initProductLists();
    this.setupProductListMediaSync();
    this.setupPlantStarterSets();
    this.setupDynamicPricingUi();
    this.setupNavigationListeners();
    this.setupThemeEditorListeners();
  }

  ensureMediaPromoBanner() {
    const promoTemplate = this.querySelector('[data-mobile-sticky-promo-template]');
    const showPromoBanner = this.dataset.mobileStickyPromoEnabled === 'true';
    const mediaGallery = this.closest('.product-information')?.querySelector('media-gallery');

    if (!showPromoBanner || !promoTemplate || !(mediaGallery instanceof HTMLElement)) return;
    if (mediaGallery.querySelector('[data-product-media-promo-banner]')) return;

    const promoTextColor = this.dataset.mobileStickyPromoTextColor?.trim();
    const promoBgColor = this.dataset.mobileStickyPromoBgColor?.trim();
    const promoStyle = [
      promoTextColor ? `color: ${promoTextColor};` : '',
      promoBgColor ? `background-color: ${promoBgColor};` : '',
    ]
      .filter(Boolean)
      .join(' ');

    mediaGallery.insertAdjacentHTML(
      'beforeend',
      `<div class="product-information__media-promo multi-step-form__mobile-sticky-promo" data-product-media-promo-banner style="${promoStyle}">${promoTemplate.innerHTML}</div>`
    );
  }

  initProductLists() {
    const productLists = this.querySelectorAll('.product-list');

    productLists.forEach((productList, index) => {
      const listId = productList.dataset.productListId || `list-${index}`;
      const groupName = `selected_product_${listId}`;

      productList.dataset.groupName = groupName;

      const inputs = productList.querySelectorAll('.product-list__input');
      let hasDefault = false;

      inputs.forEach(input => {
        input.name = groupName;

        if (input.dataset.defaultSelected === 'true' && !hasDefault) {
          input.checked = true;
          hasDefault = true;
        }
      });

      if (!hasDefault && inputs.length > 0) {
        inputs[0].checked = true;
      }
    });
  }

  setupProductListMediaSync() {
    const productLists = Array.from(this.querySelectorAll('.product-list[data-change-product-media="true"]'));

    if (productLists.length === 0) return;

    this.cacheOriginalProductMediaGallery();
    const idlePreloadUrls = [];

    productLists.forEach((productList) => {
      productList.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.checked !== true) return;

        this.syncProductMediaFromSelection(input);
        this.syncProductCopyFromSelection(input);
        this.syncPlantStarterSetVisibility(input.dataset.productHandle);
        this.updateDynamicPricingUi();
      });

      const productInputs = productList.querySelectorAll('.product-list__input');
      productInputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        if (input.dataset.noProduct === 'true' || !input.dataset.productUrl || input.checked) return;

        idlePreloadUrls.push(input.dataset.productUrl);
      });
    });

    const activeStepSelectedInput = this.getSelectedProductMediaInput();
    if (activeStepSelectedInput?.dataset.productUrl) {
      this.fetchAndStoreProductMediaGallery(activeStepSelectedInput.dataset.productUrl).catch((error) => {
        console.error('Error preloading selected product media:', error);
      });
    }

    this.syncProductMediaFromCurrentStep();
    if (activeStepSelectedInput) {
      this.syncProductCopyFromSelection(activeStepSelectedInput);
    }

    this.preloadProductMediaGalleriesOnIdle(idlePreloadUrls);
  }

  setupPlantStarterSets() {
    const plantStarterSets = this.querySelectorAll('[data-plant-starter-set]');
    if (plantStarterSets.length === 0) return;

    const primaryProductList = this.querySelector('.product-list[data-change-product-media="true"] .product-list__input:checked');
    this.syncPlantStarterSetVisibility(primaryProductList?.dataset.productHandle);
  }

  // Heading-type copy (titles, taglines): the target already wraps its content in
  // one tag (e.g. <h1>, <h3>) that carries the theme's styling. Preserve that tag
  // and replace only its text, rather than overwriting the whole container.
  applyHeadingCopy(target, newText) {
    if (!target || newText === undefined) return;
    const textTarget = target.firstElementChild || target;
    textTarget.textContent = newText;
  }

  // Prose-type copy (descriptions): may be one paragraph or several with no
  // single reliable wrapper, so replace the target's entire contents directly -
  // matching how these blocks render with no wrapper of their own today.
  applyProseCopy(target, newHtml) {
    if (!target || newHtml === undefined) return;
    target.innerHTML = newHtml;
  }

  syncProductCopyFromSelection(input) {
    const productListItem = input.closest('.product-list-item');
    const template = productListItem?.querySelector('template[data-product-copy]');
    if (!template) return;

    const root = this.closest('.shopify-section') || document;

    this.applyHeadingCopy(root.querySelector('#multi-step-product-title'), template.dataset.copyTitle);
    this.applyHeadingCopy(root.querySelector('#multi-step-product-tagline'), template.dataset.copyTagline);
    this.applyProseCopy(root.querySelector('#multi-step-product-description'), template.innerHTML);

    // Third-party review snippet: lives above the header title, not on the
    // selector card. A separate template (rather than a field on the one
    // above) since it may hold arbitrary widget markup/scripts.
    const reviewTemplate = productListItem?.querySelector('template[data-product-review]');
    if (reviewTemplate) {
      this.applyProseCopy(root.querySelector('#multi-step-product-review'), reviewTemplate.innerHTML);
    }
  }

  syncStepTwoCopyFromPlantStarterSet(plantStarterSet) {
    if (!plantStarterSet) return;

    const root = this.closest('.shopify-section') || document;
    const stepCopyTemplate = plantStarterSet.querySelector('template[data-step-copy]');

    this.applyHeadingCopy(root.querySelector('#multi-step-step2-title'), plantStarterSet.dataset.stepTitle);
    if (stepCopyTemplate) {
      this.applyProseCopy(root.querySelector('#multi-step-step2-description'), stepCopyTemplate.innerHTML);
    }
  }

  syncPlantStarterSetVisibility(selectedHandle) {
    const plantStarterSets = Array.from(this.querySelectorAll('[data-plant-starter-set]'));
    if (plantStarterSets.length === 0) return;

    if (plantStarterSets.length === 1) {
      const [onlySet] = plantStarterSets;
      onlySet.hidden = false;
      onlySet.dataset.plantStarterSetActive = 'true';
      this.syncStepTwoCopyFromPlantStarterSet(onlySet);
      return;
    }

    let matchedSet = selectedHandle
      ? plantStarterSets.find((set) => set.dataset.productHandle === selectedHandle)
      : null;

    if (!matchedSet) {
      if (selectedHandle) {
        console.warn(
          `[multi-step-form] No Plant Starter Set block found with data-product-handle="${selectedHandle}". ` +
          'Falling back to the first starter set. Check that a Plant Starter Set block\'s product matches the selected product option.'
        );
      }
      [matchedSet] = plantStarterSets;
    }

    plantStarterSets.forEach((set) => {
      const isMatch = set === matchedSet;
      set.hidden = !isMatch;
      set.dataset.plantStarterSetActive = isMatch ? 'true' : 'false';
    });

    this.syncStepTwoCopyFromPlantStarterSet(matchedSet);
  }

  setupDynamicPricingUi() {
    this.pricingSummaryEnabled = this.dataset.pricingSummaryEnabled !== 'false';
    this.setupDynamicPricingListeners();
    if (this.pricingSummaryEnabled) {
      this.ensureStepPricingSummary();
    }
    this.ensureMobileStickyCta();
    this.updateDynamicPricingUi();
  }

  setupDynamicPricingListeners() {
    if (this.dynamicPricingListenersBound) return;

    const productLists = this.querySelectorAll('.product-list');
    productLists.forEach((productList) => {
      productList.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.checked !== true) return;

        this.updateDynamicPricingUi();
      });
    });

    this.addEventListener('plant-starter-set:change', () => {
      this.updateDynamicPricingUi();
    });

    this.dynamicPricingListenersBound = true;
  }

  ensureStepPricingSummary() {
    if (this.stepPricingSummaryElement) return;

    const summary = document.createElement('div');
    summary.className = 'multi-step-form__pricing-summary';
    summary.hidden = true;
    summary.innerHTML = `
      <div class="multi-step-form__pricing-summary-main">
        <span class="multi-step-form__pricing-price" data-pricing-price></span>
        <span class="multi-step-form__pricing-compare" data-pricing-compare></span>
      </div>
      <div class="multi-step-form__pricing-savings" data-pricing-savings hidden></div>
    `;
    this.stepPricingSummaryElement = summary;
  }

  ensureMobileStickyCta() {
    if (this.mobileStickyCtaElement) return;

    const stickyCta = document.createElement('div');
    stickyCta.className = 'multi-step-form__mobile-sticky-cta';
    stickyCta.hidden = true;
    const promoTemplate = this.querySelector('[data-mobile-sticky-promo-template]');
    const showPromoBanner = this.dataset.mobileStickyPromoEnabled === 'true';
    const promoTextColor = this.dataset.mobileStickyPromoTextColor?.trim();
    const promoBgColor = this.dataset.mobileStickyPromoBgColor?.trim();
    const promoStyle = [
      promoTextColor ? `color: ${promoTextColor};` : '',
      promoBgColor ? `background-color: ${promoBgColor};` : '',
    ]
      .filter(Boolean)
      .join(' ');

    stickyCta.innerHTML = `
      ${showPromoBanner && promoTemplate ? `<div class="multi-step-form__mobile-sticky-promo" style="${promoStyle}">${promoTemplate.innerHTML}</div>` : ''}
      <div class="multi-step-form__mobile-sticky-cta-main">
        <div class="multi-step-form__mobile-sticky-summary">
          <div class="multi-step-form__mobile-sticky-price-row">
            <span class="multi-step-form__mobile-sticky-price" data-sticky-price></span>
            <span class="multi-step-form__mobile-sticky-compare" data-sticky-compare></span>
          </div>
        </div>
        <button type="button" class="multi-step-form__mobile-sticky-button primary-btn" data-sticky-button></button>
      </div>
    `;

    const stickyButton = stickyCta.querySelector('[data-sticky-button]');
    stickyButton?.addEventListener('click', () => {
      const currentPrimaryButton = this.getCurrentPrimaryButton();
      if (currentPrimaryButton instanceof HTMLButtonElement) {
        currentPrimaryButton.click();
      }
    });

    document.body.appendChild(stickyCta);
    this.mobileStickyCtaElement = stickyCta;
  }

  updateDynamicPricingUi() {
    this.updateCurrentStepProductListOptionPricing();
    this.updateCurrentStepButtonLabels();
    this.renderCurrentStepPricingSummary();
    this.updateMobileStickyCtaContent();
    this.observeCurrentPrimaryButton();
  }

  getCurrentPrimaryButton() {
    const currentStep = this.getCurrentStepElement();
    if (!(currentStep instanceof HTMLElement)) return null;

    return currentStep.querySelector('[data-submit-button], [data-next-button]');
  }

  getSelectedProductInputs() {
    return Array.from(this.querySelectorAll('.product-list__input:checked')).filter(
      (input) => input instanceof HTMLInputElement
    );
  }

  getSelectedInputTotals() {
    return this.getSelectedProductInputs().reduce(
      (totals, input) => {
        const price = Number(input.dataset.variantPrice || input.dataset.productPrice || 0);
        const compare = Number(input.dataset.variantComparePrice || input.dataset.productComparePrice || 0);

        totals.price += price;
        totals.compare += compare > 0 ? compare : price;
        return totals;
      },
      { price: 0, compare: 0 }
    );
  }

  formatPrice(amount) {
    if (!amount || amount <= 0) return '';

    return formatMoney(amount, this.moneyFormat, this.currency).replace(/\.00(\D*)$/, '$1');
  }

  getCurrentStepSelectedInput() {
    const currentStep = this.getCurrentStepElement();
    if (!(currentStep instanceof HTMLElement)) return null;

    return currentStep.querySelector('.product-list__input:checked');
  }

  getCurrentStepSelectedItemLabel() {
    const currentStep = this.getCurrentStepElement();
    if (!(currentStep instanceof HTMLElement)) return '';

    const selectedProductListInput = currentStep.querySelector('.product-list__input:checked');
    if (selectedProductListInput instanceof HTMLInputElement) {
      return selectedProductListInput.dataset.productTitle?.trim() || '';
    }

    const activePlantStarterButton = currentStep.querySelector('.plant-starter-set__option-btn.active');
    if (activePlantStarterButton instanceof HTMLElement) {
      return activePlantStarterButton.textContent?.trim() || '';
    }

    return '';
  }

  updateCurrentStepButtonLabels() {
    const currentStep = this.getCurrentStepElement();
    if (!(currentStep instanceof HTMLElement)) return;

    const selectedItemLabel = this.getCurrentStepSelectedItemLabel();
    const templatedButtons = currentStep.querySelectorAll('[data-label-template]');

    templatedButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;

      const template = button.dataset.labelTemplate || '';
      if (!template.includes('[SELECTED_ITEM]')) {
        button.textContent = template;
        return;
      }

      if (!selectedItemLabel) {
        button.textContent = template.replaceAll('[SELECTED_ITEM]', '').replace(/\s{2,}/g, ' ').trim();
        return;
      }

      button.textContent = template.replaceAll('[SELECTED_ITEM]', selectedItemLabel);
    });
  }

  ensureDynamicPriceColumn(label) {
    let priceColumn = label.querySelector('.product-list-item__price-column--dynamic');
    if (priceColumn) return priceColumn;

    priceColumn = document.createElement('div');
    priceColumn.className = 'product-list-item__price-column product-list-item__price-column--dynamic';
    priceColumn.innerHTML = `
      <div class="product-list-item__price-wrapper">
        <span class="product-list-item__price" data-dynamic-option-price></span>
        <span class="product-list-item__compare-price" data-dynamic-option-compare></span>
      </div>
    `;
    label.appendChild(priceColumn);

    return priceColumn;
  }

  updateCurrentStepProductListOptionPricing() {
    const currentStep = this.getCurrentStepElement();
    if (!(currentStep instanceof HTMLElement)) return;

    const productLists = currentStep.querySelectorAll('.product-list');
    productLists.forEach((productList) => {
      const inputs = productList.querySelectorAll('.product-list__input');
      inputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;

        const label = currentStep.querySelector(`label[for="${input.id}"]`);
        if (!(label instanceof HTMLElement)) return;

        const hasStaticPriceColumn = label.querySelector(
          '.product-list-item__price-column:not(.product-list-item__price-column--dynamic)'
        );
        if (hasStaticPriceColumn) return;

        const priceColumn = this.ensureDynamicPriceColumn(label);
        const priceElement = priceColumn.querySelector('[data-dynamic-option-price]');
        const compareElement = priceColumn.querySelector('[data-dynamic-option-compare]');
        const price = Number(input.dataset.variantPrice || input.dataset.productPrice || 0);
        const compare = Number(input.dataset.variantComparePrice || input.dataset.productComparePrice || 0);

        if (price > 0) {
          priceElement.textContent = this.formatPrice(price);
          priceColumn.hidden = false;
        } else {
          priceElement.textContent = '';
          priceColumn.hidden = true;
        }

        if (compare > price) {
          compareElement.textContent = this.formatPrice(compare);
          compareElement.hidden = false;
        } else {
          compareElement.textContent = '';
          compareElement.hidden = true;
        }
      });
    });
  }

  renderCurrentStepPricingSummary() {
    const currentStep = this.getCurrentStepElement();
    const primaryButton = this.getCurrentPrimaryButton();
    if (!(currentStep instanceof HTMLElement) || !(primaryButton instanceof HTMLElement) || !this.stepPricingSummaryElement || !this.pricingSummaryEnabled) return;

    const isFinalStep = primaryButton.matches('[data-submit-button]');
    if (!isFinalStep) {
      this.stepPricingSummaryElement.hidden = true;
      this.stepPricingSummaryElement.remove();
      return;
    }

    const totals = this.getSelectedInputTotals();
    const currentStepSelectedInput = this.getCurrentStepSelectedInput();
    const savingsText = currentStepSelectedInput?.dataset.badgeText?.trim() || '';
    const priceElement = this.stepPricingSummaryElement.querySelector('[data-pricing-price]');
    const compareElement = this.stepPricingSummaryElement.querySelector('[data-pricing-compare]');
    const savingsElement = this.stepPricingSummaryElement.querySelector('[data-pricing-savings]');

    priceElement.textContent = this.formatPrice(totals.price);

    if (totals.compare > totals.price) {
      compareElement.textContent = this.formatPrice(totals.compare);
      compareElement.hidden = false;
    } else {
      compareElement.textContent = '';
      compareElement.hidden = true;
    }

    if (savingsText) {
      savingsElement.textContent = savingsText;
      savingsElement.hidden = false;
    } else {
      savingsElement.textContent = '';
      savingsElement.hidden = true;
    }

    primaryButton.parentNode.insertBefore(this.stepPricingSummaryElement, primaryButton);
    this.stepPricingSummaryElement.hidden = false;
  }

  updateMobileStickyCtaContent() {
    if (!this.mobileStickyCtaElement) return;

    const currentStep = this.getCurrentStepElement();
    const primaryButton = this.getCurrentPrimaryButton();
    if (!(currentStep instanceof HTMLElement) || !(primaryButton instanceof HTMLButtonElement)) {
      this.mobileStickyCtaElement.hidden = true;
      return;
    }

    const selectedInput = this.getCurrentStepSelectedInput();
    const summaryElement = this.mobileStickyCtaElement.querySelector('.multi-step-form__mobile-sticky-summary');
    const priceElement = this.mobileStickyCtaElement.querySelector('[data-sticky-price]');
    const compareElement = this.mobileStickyCtaElement.querySelector('[data-sticky-compare]');
    const buttonElement = this.mobileStickyCtaElement.querySelector('[data-sticky-button]');
    const isFinalStep = primaryButton.matches('[data-submit-button]');
    const totals = this.getSelectedInputTotals();
    const currentPrice = isFinalStep
      ? totals.price
      : Number(selectedInput?.dataset.variantPrice || selectedInput?.dataset.productPrice || 0);
    const currentCompare = isFinalStep
      ? totals.compare
      : Number(selectedInput?.dataset.variantComparePrice || selectedInput?.dataset.productComparePrice || 0);
    const title = isFinalStep ? (totals.price > 0 ? 'Selected total' : '') : selectedInput?.dataset.productTitle || '';

    priceElement.textContent = this.formatPrice(currentPrice);
    summaryElement.hidden = !priceElement.textContent;

    if (currentCompare > currentPrice) {
      compareElement.textContent = this.formatPrice(currentCompare);
      compareElement.hidden = false;
    } else {
      compareElement.textContent = '';
      compareElement.hidden = true;
    }

    buttonElement.textContent = primaryButton.textContent.trim();
  }

  observeCurrentPrimaryButton() {
    if (this.mobileStickyCtaObserver) {
      this.mobileStickyCtaObserver.disconnect();
      this.mobileStickyCtaObserver = null;
    }

    const primaryButton = this.getCurrentPrimaryButton();
    if (!(primaryButton instanceof HTMLElement) || !this.mobileStickyCtaElement) return;

    this.mobileStickyCtaObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const shouldShow = window.innerWidth < 750 && !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.mobileStickyCtaElement.hidden = !shouldShow;
      },
      { threshold: 0 }
    );

    this.mobileStickyCtaObserver.observe(primaryButton);
  }

  getCurrentStepElement() {
    if (!this.steps?.length || this.currentStep < 1) return null;

    return this.steps[this.currentStep - 1] ?? null;
  }

  getSelectedProductMediaInput() {
    // Searches the whole form, not just the currently active step: the device
    // picker only lives in step 1, but the gallery it drives needs to keep
    // showing that selection on every later step too. Scoping this to
    // getCurrentStepElement() (as it used to be) meant that as soon as you left
    // step 1, this always found nothing and fell back to restoring the page's
    // original product gallery, undoing whatever was picked in step 1.
    const productLists = this.querySelectorAll('.product-list[data-change-product-media="true"]');
    for (const productList of productLists) {
      const selectedInput = productList.querySelector('.product-list__input:checked');
      if (selectedInput instanceof HTMLInputElement) {
        return selectedInput;
      }
    }

    return null;
  }

  syncProductMediaFromCurrentStep() {
    const selectedInput = this.getSelectedProductMediaInput();
    if (selectedInput) {
      this.syncProductMediaFromSelection(selectedInput);
      return;
    }

    this.restoreOriginalProductMediaGallery();
  }

  normalizeProductMediaKey(productUrl) {
    if (!productUrl) return this.originalProductMediaKey;

    try {
      return new URL(productUrl, window.location.origin).pathname;
    } catch (_error) {
      return productUrl;
    }
  }

  cacheOriginalProductMediaGallery() {
    const mediaGallery = this.getCurrentProductMediaGallery();
    if (!mediaGallery) return;

    mediaGallery.dataset.productMediaKey = this.originalProductMediaKey;
    mediaGallery.hidden = false;
    mediaGallery.style.display = '';
    mediaGallery.setAttribute('aria-hidden', 'false');
    this.productMediaGalleryCache.set(this.originalProductMediaKey, mediaGallery);
  }

  getCurrentProductMediaGallery() {
    const mediaRoot = this.getProductMediaRoot();
    if (!mediaRoot) return null;

    return mediaRoot.querySelector('media-gallery:not([hidden])') || mediaRoot.querySelector('media-gallery');
  }

  getProductMediaRoot() {
    const closestSection = this.closest('.shopify-section');
    return closestSection?.querySelector('.product-information__media') ?? document.querySelector('.product-information__media');
  }

  showProductMediaGallery(mediaKey) {
    const mediaRoot = this.getProductMediaRoot();
    if (!mediaRoot) return;

    const galleries = mediaRoot.querySelectorAll('media-gallery[data-product-media-key]');
    galleries.forEach((gallery) => {
      const isVisible = gallery.dataset.productMediaKey === mediaKey;
      gallery.hidden = !isVisible;
      gallery.style.display = isVisible ? '' : 'none';
      gallery.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    });
  }

  restoreOriginalProductMediaGallery() {
    this.showProductMediaGallery(this.originalProductMediaKey);
  }

  async fetchAndStoreProductMediaGallery(productUrl) {
    const mediaKey = this.normalizeProductMediaKey(productUrl);
    const cachedGallery = this.productMediaGalleryCache.get(mediaKey);
    if (cachedGallery) return cachedGallery;

    const existingRequest = this.productMediaGalleryRequests.get(mediaKey);
    if (existingRequest) return existingRequest;

    const request = fetch(productUrl, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch product media: ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const documentFragment = parser.parseFromString(html, 'text/html');
        const newMediaGallery = documentFragment.querySelector('.product-information__media media-gallery, media-gallery');
        const mediaRoot = this.getProductMediaRoot();

        if (!newMediaGallery || !mediaRoot) return null;

        newMediaGallery.dataset.productMediaKey = mediaKey;
        newMediaGallery.hidden = true;
        newMediaGallery.style.display = 'none';
        newMediaGallery.setAttribute('aria-hidden', 'true');
        mediaRoot.appendChild(newMediaGallery);
        this.productMediaGalleryCache.set(mediaKey, newMediaGallery);

        return newMediaGallery;
      })
      .finally(() => {
        this.productMediaGalleryRequests.delete(mediaKey);
      });

    this.productMediaGalleryRequests.set(mediaKey, request);
    return request;
  }

  preloadProductMediaGalleriesOnIdle(productUrls) {
    const uniqueProductUrls = [...new Set(productUrls.filter(Boolean))];
    if (uniqueProductUrls.length === 0) return;

    const scheduleIdleTask = window.requestIdleCallback
      ? (callback) => window.requestIdleCallback(callback, { timeout: 1500 })
      : (callback) => window.setTimeout(callback, 150);

    const preloadNext = (index) => {
      if (index >= uniqueProductUrls.length) return;

      scheduleIdleTask(() => {
        this.fetchAndStoreProductMediaGallery(uniqueProductUrls[index])
          .catch((error) => {
            console.error('Error preloading product media gallery:', error);
          })
          .finally(() => {
            preloadNext(index + 1);
          });
      });
    };

    preloadNext(0);
  }

  async syncProductMediaFromSelection(input) {
    const productUrl = input.dataset.productUrl;
    const isNoProduct = input.dataset.noProduct === 'true';
    const requestId = ++this.productMediaUpdateRequestId;

    if (isNoProduct || !productUrl) {
      this.restoreOriginalProductMediaGallery();
      return;
    }

    const mediaKey = this.normalizeProductMediaKey(productUrl);

    if (this.productMediaGalleryCache.has(mediaKey)) {
      this.showProductMediaGallery(mediaKey);
      return;
    }

    try {
      const newMediaGallery = await this.fetchAndStoreProductMediaGallery(productUrl);
      if (requestId !== this.productMediaUpdateRequestId) return;

      if (!newMediaGallery) return;

      this.showProductMediaGallery(mediaKey);
    } catch (error) {
      console.error('Error updating product media from product list selection:', error);
    }
  }

  setupNavigationListeners() {
    this.form?.addEventListener('click', (e) => {
      const prevBtn = e.target.closest('[data-prev-button]');
      const nextBtn = e.target.closest('[data-next-button]');
      const submitBtn = e.target.closest('[data-submit-button]');

      if (prevBtn) {
        e.preventDefault();
        this.prevStep();
      } else if (nextBtn) {
        e.preventDefault();
        this.nextStep();
      } else if (submitBtn) {
        e.preventDefault();
        this.handleSubmit(e);
      }
    });

    // Prevent form submission via Enter key - only allow submit button click
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
    });
  }

  updateSteps() {
    this.steps = this.stepsContainer.querySelectorAll('.multi-step-form-step, [data-block-type="multi-step-form-step"]');
    
    if (this.steps.length === 0) {
      this.steps = this.stepsContainer.children;
    }
    
    this.totalSteps = this.steps.length;

    if (this.totalSteps > 0) {
      this.wrapSteps();
      this.injectStepOnlyStyles();
      this.renderProgress();
      this.showStep(1);
    }
  }

  setupThemeEditorListeners() {
    if (!Shopify.designMode) return;

    document.addEventListener('shopify:block:select', this.handleEditorBlockSelect);
  }

  handleEditorBlockSelect(event) {
    const selectedBlock = event.target;
    if (!(selectedBlock instanceof HTMLElement) || !this.contains(selectedBlock)) return;

    const selectedStep = selectedBlock.closest('.multi-step-form__step');
    if (!(selectedStep instanceof HTMLElement)) return;

    const stepNumber = parseInt(selectedStep.dataset.step || '', 10);
    if (!Number.isNaN(stepNumber)) {
      this.showStep(stepNumber);
      selectedStep.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }

  wrapSteps() {
    for (let i = 0; i < this.steps.length; i++) {
      const child = this.steps[i];
      if (!child.classList.contains('multi-step-form__step')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'multi-step-form__step';
        wrapper.dataset.step = i + 1;
        wrapper.style.display = 'none';
        child.parentNode.insertBefore(wrapper, child);
        wrapper.appendChild(child);
      } else {
        child.dataset.step = i + 1;
        child.style.display = 'none';
      }
    }
    this.steps = this.stepsContainer.querySelectorAll('.multi-step-form__step');
  }

  renderProgress() {
    this.progressBar.innerHTML = '';

    for (let i = 1; i <= this.totalSteps; i++) {
      const step = document.createElement('div');
      step.className = `multi-step-form__progress-step ${i === 1 ? 'active' : ''}`;
      step.dataset.step = i;
      step.innerHTML = `<span class="multi-step-form__progress-indicator">${i}</span>`;
      this.progressBar.appendChild(step);

      if (i < this.totalSteps) {
        const line = document.createElement('div');
        line.className = `multi-step-form__progress-line ${i === 1 ? 'active' : ''}`;
        line.dataset.line = i;
        this.progressBar.appendChild(line);
      }
    }
  }

  injectStepOnlyStyles() {
    if (!document.head || this.totalSteps === 0) return;

    const styleId = 'multi-step-form-step-only-styles';
    let css = `
      [class*='step-'][class*='-only'] {
        display: none !important;
      }
    `;

    for (let step = 1; step <= this.totalSteps; step += 1) {
      css += `
        body.pdp-form-step-${step} .step-${step}-only {
          display: block !important;
        }
        body.pdp-form-step-${step} .section.step-${step}-only {
          display: grid !important;
        }
      `;
    }

    let styleTag = document.getElementById(styleId);
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.textContent = css;
  }

  updateStepState(stepNumber) {
    this.dataset.currentStep = String(stepNumber);
    this.classList.forEach((className) => {
      if (className.startsWith('multi-step-form--step-')) {
        this.classList.remove(className);
      }
    });
    this.classList.add(`multi-step-form--step-${stepNumber}`);

    if (document.body) {
      Array.from(document.body.classList).forEach((className) => {
        if (className.startsWith('pdp-form-step-')) {
          document.body.classList.remove(className);
        }
      });
      document.body.dataset.pdpFormStep = String(stepNumber);
      document.body.classList.add(`pdp-form-step-${stepNumber}`);
    }

    this.dispatchEvent(new CustomEvent('multi-step-form:change', {
      bubbles: true,
      detail: {
        currentStep: stepNumber,
        totalSteps: this.totalSteps,
        form: this,
      },
    }));
  }

  showStep(stepNumber) {
    this.steps.forEach((step, index) => {
      const stepNum = parseInt(step.dataset.step || index + 1);
      step.style.display = stepNum === stepNumber ? 'block' : 'none';
    });

    this.currentStep = stepNumber;

    const currentStep = this.steps[stepNumber - 1];
    if (currentStep) {
      const prevButtons = currentStep.querySelectorAll('[data-prev-button]');
      prevButtons.forEach(btn => {
        btn.style.display = stepNumber === 1 ? 'none' : 'flex';
      });

      const nextButtons = currentStep.querySelectorAll('[data-next-button]');
      nextButtons.forEach(btn => {
        btn.style.display = stepNumber === this.totalSteps ? 'none' : 'flex';
      });

      const submitButtons = currentStep.querySelectorAll('[data-submit-button]');
      submitButtons.forEach(btn => {
        btn.style.display = stepNumber === this.totalSteps ? 'flex' : 'none';
      });
    }

    const progressSteps = this.progressBar.querySelectorAll('.multi-step-form__progress-step');
    progressSteps.forEach((step, index) => {
      step.classList.toggle('active', index + 1 <= stepNumber);
    });

    const progressLines = this.progressBar.querySelectorAll('.multi-step-form__progress-line');
    progressLines.forEach((line, index) => {
      line.classList.toggle('active', index + 1 < stepNumber);
    });

    this.syncProductMediaFromCurrentStep();
    this.updateDynamicPricingUi();
    this.updateStepState(stepNumber);
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      this.showStep(this.currentStep + 1);
      if (window.innerWidth < 750) {
        requestAnimationFrame(() => this.scrollToFormTop());
      }
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.showStep(this.currentStep - 1);
      if (window.innerWidth < 750) {
        requestAnimationFrame(() => this.scrollToFormTop());
      }
    }
  }

  scrollToFormTop() {
    if (window.innerWidth >= 750) return;
    const productDetails = this.closest('.product-details');
    if (!productDetails) return;

    const bodyStyles = getComputedStyle(document.body);
    const headerOffset =
      parseInt(bodyStyles.getPropertyValue('--header-group-height')) ||
      parseInt(bodyStyles.getPropertyValue('--header-height')) ||
      0;

    // Extra offset lets us scroll slightly past the top of product-details
    // (useful on mobile where the media sits above details).
    const extraOffset = Number(this.dataset.scrollExtraOffset || 0) || 0;
    const targetTop = productDetails.getBoundingClientRect().top + window.scrollY - headerOffset + extraOffset;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: 'smooth'
    });
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }

  setLoadingState(isLoading) {
    const submitButtons = this.form.querySelectorAll('[data-submit-button]');
    submitButtons.forEach(button => {
      button.disabled = isLoading;
      if (isLoading) {
        button.dataset.originalText = button.textContent;
        button.textContent = 'Adding...';
        button.classList.add('loading');
      } else {
        button.textContent = button.dataset.originalText;
        button.classList.remove('loading');
      }
    });
  }

  handleSubmit(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    const productLists = this.querySelectorAll('.product-list');
    const plantStarterSets = this.querySelectorAll('[data-plant-starter-set]');
    const cartItems = [];
    let hasSelection = false;

    const groupId = `multi-step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const quantity = parseInt(formData.get('quantity') || 1);
    const bundledVariantIds = new Set();
    let groupSequence = 0;

    const pushCartItem = (variantId, options = {}) => {
      if (!variantId) return;

      const normalizedVariantId = `${variantId}`.trim();
      if (!normalizedVariantId) return;

      const properties = {
        _gardyn_bundle_group: groupId
      };

      if (options.hiddenBundle === true) {
        properties._hidden_bundle = 'true';
      }

      if (options.groupSequence) {
        properties._group_sequence = String(options.groupSequence);
      }

      cartItems.push({
        id: normalizedVariantId,
        quantity,
        properties
      });
    };

    productLists.forEach(productList => {
      const groupName = productList.dataset.groupName;
      if (!groupName) return;

      const selectedInput = productList.querySelector(`input[name="${groupName}"]:checked`);
      
      if (selectedInput) {
        hasSelection = true;
        const isNoProduct = selectedInput.dataset.noProduct === 'true';
        const bundleVariantIds = (selectedInput.dataset.bundleVariantIds || '')
          .split(',')
          .map(id => id.trim())
          .filter(Boolean);
        const itemGroupSequence = groupSequence + 1;
        
        if (!isNoProduct && selectedInput.value) {
          pushCartItem(selectedInput.value, { groupSequence: itemGroupSequence });
          bundledVariantIds.add(`${selectedInput.value}`.trim());
          groupSequence = itemGroupSequence;
        }

        bundleVariantIds.forEach((bundleVariantId) => {
          if (bundledVariantIds.has(bundleVariantId)) return;

          pushCartItem(bundleVariantId, { hiddenBundle: true, groupSequence: itemGroupSequence });
          bundledVariantIds.add(bundleVariantId);
        });

        if (isNoProduct && bundleVariantIds.length > 0) {
          groupSequence = itemGroupSequence;
        }
      }
    });

    plantStarterSets.forEach(plantSet => {
      // Skip starter sets hidden by product-selection matching (see
      // syncPlantStarterSetVisibility) so an inactive starter set for a
      // non-selected product never gets added to cart alongside the real one.
      if (plantSet.hidden || plantSet.dataset.plantStarterSetActive === 'false') return;

      const selectedVariantInput = plantSet.querySelector('[data-plant-starter-set-input]');
      
      if (selectedVariantInput && selectedVariantInput.value) {
        hasSelection = true;
        const normalizedVariantId = `${selectedVariantInput.value}`.trim();
        const itemGroupSequence = groupSequence + 1;
        if (!bundledVariantIds.has(normalizedVariantId)) {
          pushCartItem(normalizedVariantId, { groupSequence: itemGroupSequence });
          bundledVariantIds.add(normalizedVariantId);
          groupSequence = itemGroupSequence;
        }
      }
    });

    if (!hasSelection) {
      alert('Please select a product before submitting.');
      return;
    }

    this.setLoadingState(true);

    if (cartItems.length === 0) {
      this.openCartDrawer();
      this.setLoadingState(false);
      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });


    // Reversed to force device -> starter set order
    const payload = {
      items: cartItems.slice().reverse(),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload)
    })
    .then(response => response.json())
    .then(data => {
      if (data.status && data.status !== 200) {
        throw new Error(data.description || 'Error adding product to cart');
      }
      
      document.dispatchEvent(new CartAddEvent(data, 'multi-step-form', {
        source: 'multi-step-form',
        sections: data.sections,
      }));
      this.openCartDrawer();
      this.setLoadingState(false);
    })
    .catch(error => {
      this.setLoadingState(false);
      console.error('Error adding to cart:', error);
      alert('There was an error adding the product to cart. Please try again.');
    });
  }

  disconnectedCallback() {
    this.mobileStickyCtaObserver?.disconnect();
    document.removeEventListener('shopify:block:select', this.handleEditorBlockSelect);
  }
}

customElements.define('multi-step-form', MultiStepForm);
